import type { Coordinates, LocationProvider } from '@/application/ports/LocationProvider';

/**
 * The browser's own location, via the Geolocation API.
 *
 * Every failure resolves to null rather than rejecting. A refused permission, a
 * timeout and a device with no GPS are the same thing to the caller: we do not
 * know where this is. The geofence treats null as a refusal, so distinguishing
 * them would add branches and change nothing.
 *
 * `enableHighAccuracy` is on because the radius is measured in hundreds of
 * metres and a coarse network fix can be off by more than that, which would
 * refuse somebody standing in the lab.
 */
const TIMEOUT_MS = 10_000;

export class BrowserLocationProvider implements LocationProvider {
  current(): Promise<Coordinates | null> {
    if (typeof navigator === 'undefined' || !('geolocation' in navigator)) {
      return Promise.resolve(null);
    }

    return new Promise((resolve) => {
      navigator.geolocation.getCurrentPosition(
        (position) =>
          resolve({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
          }),
        () => resolve(null),
        { enableHighAccuracy: true, timeout: TIMEOUT_MS, maximumAge: 60_000 },
      );
    });
  }
}
